import { Injectable, NotFoundException } from '@nestjs/common';
import { InjectModel } from '@nestjs/mongoose';
import { Model } from 'mongoose';
import { Feature } from './schemas/feature.schema';
import { CreateFeatureDto } from './dto/create-feature.dto';
import { UpdateFeatureDto } from './dto/update-feature.dto';

@Injectable()
export class FeatureService {
  constructor(
    @InjectModel(Feature.name) private featureModel: Model<Feature>,
  ) {}

  async create(createFeatureDto: CreateFeatureDto): Promise<Feature> {
    const createdFeature = new this.featureModel(createFeatureDto);
    return createdFeature.save();
  }

  async findAll(): Promise<Feature[]> {
    return this.featureModel.find().sort({ sortOrder: 1 }).exec();
  }

  async findActive(): Promise<Feature[]> {
    return this.featureModel.find({ isActive: true }).sort({ sortOrder: 1 }).exec();
  }

  async findOne(id: string): Promise<Feature> {
    const feature = await this.featureModel.findById(id).exec();
    if (!feature) {
      throw new NotFoundException(`Feature with ID ${id} not found`);
    }
    return feature;
  }

  async update(id: string, updateFeatureDto: UpdateFeatureDto): Promise<Feature> {
    const updatedFeature = await this.featureModel
      .findByIdAndUpdate(id, updateFeatureDto, { new: true })
      .exec();
    if (!updatedFeature) {
      throw new NotFoundException(`Feature with ID ${id} not found`);
    }
    return updatedFeature;
  }

  async remove(id: string): Promise<void> {
    const result = await this.featureModel.findByIdAndDelete(id).exec();
    if (!result) {
      throw new NotFoundException(`Feature with ID ${id} not found`);
    }
  }

  async toggleActive(id: string): Promise<Feature> {
    const feature = await this.findOne(id);
    feature.isActive = !feature.isActive;
    return feature.save();
  }

  async updateSortOrder(id: string, sortOrder: number): Promise<Feature> {
    const feature = await this.findOne(id);
    feature.sortOrder = sortOrder;
    return feature.save();
  }
} 